import React, { PureComponent } from "react";
import { Link } from "react-router";
import moment from "moment";

import EventTable from "./event-table";
import Auth from "../../routes/Auth";
import Teams from "../../api/Teams";
import { getRequest, batchRequest } from "../../api/ApiService";
import { fetchTeamDecisions } from "../../api/TeamDecisionsService";

const numberWithCommas = x =>
  x != null && x !== "" ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";

const fmtMoney = v => `$${numberWithCommas(Math.round(Number(v) || 0))}`;
const fmtMoneyDecimals = v => `$${(Number(v) || 0).toFixed(2)}`;

// The backend stores journal request bodies as a Python repr string.
// Two flavors show up in the wild:
//   - Python 2: `{u'product': u'31955', u'price': 5}`
//   - Python 3: `{'product': '31955', 'price': 5}`  ← no u-prefix
// Strip the optional u-prefix, then swap single quotes for double
// quotes so JSON.parse can read it. Also lowercase True/False/None.
const jsonDecode = str => {
  if (!str || typeof str !== "string") return {};
  try {
    const cleaned = str
      .replace(/u'/g, "'")
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, "true")
      .replace(/\bFalse\b/g, "false")
      .replace(/\bNone\b/g, "null");
    return JSON.parse(cleaned);
  } catch (e) {
    return {};
  }
};

// Journal `response` is stored server-side as a Python bytes-literal
// repr — e.g. `b'{"id":31955,"name":"Hello",...}'`. The inner payload
// is real JSON; we just need to strip the `b'`...`'` wrapper before
// JSON.parse. Also tolerates already-parsed objects (older entries)
// and plain JSON strings.
const parseJournalResponse = raw => {
  if (raw == null) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};

  let str = raw.trim();
  if ((str.startsWith("b'") && str.endsWith("'")) ||
      (str.startsWith('b"') && str.endsWith('"'))) {
    str = str.slice(2, -1);
  }
  try {
    return JSON.parse(str);
  } catch (e) {
    return jsonDecode(str);
  }
};

const fmtTimestamp = ts => moment(ts).format("MMM D, YYYY · h:mm A");

const STATUS_SUCCESS = { type: "success", label: "Success" };
const STATUS_FAILURE = { type: "failure", label: "Failed" };
const STATUS_NEUTRAL = { type: "neutral", label: "—" };

const ROLES = [
  { key: "ceo", path: "/ceo/audit-trail", label: "CEO" },
  { key: "pd",  path: "/pd/audit-trail",  label: "Head of Product" },
  { key: "pm",  path: "/pm/audit-trail",  label: "Head of Marketing" },
  { key: "op",  path: "/op/audit-trail",  label: "Head of Operations" },
  { key: "fn",  path: "/fn/audit-trail",  label: "Head of Finance" },
];

class AuditTrail extends PureComponent {
  constructor(props) {
    super(props);

    const isInstructor = Auth.isAnyInstructor();

    this.state = {
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      rounds: props.rounds,
      histories: [],
      users: [],
      team_machines: [],
      products: {},
      equity_clients: [],
      debt_clients: [],
      marketing_clients: [],
      sales_clients: [],
      distribution_centers_info: [],
      isInstructor,
    };
  }

  componentWillMount() {
    this.fetchData();
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      (this.state.rounds.length === 0 && nextProps.rounds.length !== 0)
    ) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl,
          currentRound: nextProps.currentRound,
          rounds: nextProps.rounds,
        },
        () => this.fetchData()
      );
    }
  }

  get activeRound() {
    const { currentRound, roundInUrl } = this.state;
    if (roundInUrl != null && roundInUrl !== currentRound) return roundInUrl;
    return currentRound || 0;
  }

  // Prefer a team_id passed via the URL query string (used by the
  // instructor-facing /analysis/audit-trail route, which navigates
  // here with the picked team) and fall back to the session's
  // logged-in team for the existing role-prefixed flows
  // (/ceo/audit-trail, /finance/audit-trail, …).
  resolveTeamId = () => {
    const queryTeamId = this.props.location
      && this.props.location.query
      && this.props.location.query.team_id;
    const parsed = Number(queryTeamId);
    if (queryTeamId && !isNaN(parsed) && parsed > 0) return parsed;
    return Auth.getTeamId();
  };

  fetchData = () => {
    const { isInstructor } = this.state;
    const team_id = this.resolveTeamId();

    this.getAuditTrailRequest(isInstructor, team_id)
      .then(res => {
        const histories = res
          .filter(r => r.statusCode === 200)
          .reduce((acc, r) => acc.concat(r.body), [])
          .map(h => ({ ...h, response: parseJournalResponse(h.response) }));
        this.setState({ histories });
      })
      .catch(() => {});

    // Teammate names come from Teams.players() — localStorage cache
    // populated by App.js from data.teamplayers on general-info. The
    // old /api/users/ endpoint only ever returned the signed-in user,
    // so any journal row authored by a teammate rendered as "Unknown".
    const players = Teams.players() || [];
    const users = players
      .map(p => p && p.user)
      .filter(u => u && u.id != null);
    this.setState({ users });

    this.getProductsRequest(isInstructor, team_id)
      .then(res => {
        const products = {};
        res.body.forEach(p => { products[p.id] = p.name; });
        this.setState({ products });
      })
      .catch(() => {});

    this.getTeamMachinesRequest(isInstructor, team_id)
      .then(res => this.setState({ team_machines: res.body }))
      .catch(() => {});

    this.getEquityClientsRequest()
      .then(res => this.setState({ equity_clients: res.body || [] }))
      .catch(() => {});

    this.getDebtClientsRequest()
      .then(res => this.setState({ debt_clients: res.body || [] }))
      .catch(() => {});

    // Pull marketing_clients (and sales_clients when surfaced) from
    // team-decisions role=marketing so the PM descriptions can name the
    // promo channel and sales channel rather than show raw ids.
    fetchTeamDecisions(this.activeRound, "marketing")
      .then(decisions => {
        this.setState({
          marketing_clients: decisions.marketing_clients || [],
          sales_clients: decisions.sales_clients || [],
        });
      })
      .catch(() => {});

    // Pull distribution_centers_info from team-decisions role=operations
    // so OP team-center rows can name the distribution center the user
    // selected/deleted (e.g. "Toledo", "Little Rock") instead of an id.
    fetchTeamDecisions(this.activeRound, "operations")
      .then(decisions => {
        this.setState({
          distribution_centers_info: decisions.distributionCentersInfo || [],
        });
      })
      .catch(() => {});
  };

  getEquityClientsRequest = () =>
    getRequest(`${process.env.REACT_APP_API_URL}static/equity_clients/`);

  getDebtClientsRequest = () =>
    getRequest(`${process.env.REACT_APP_API_URL}static/debt_clients/`);

  getTeamMachinesRequest = (isInstructor, team_id) =>
    getRequest(
      isInstructor
        ? `${process.env.REACT_APP_API_URL}team/machines/?team_id=${team_id}`
        : `${process.env.REACT_APP_API_URL}team/machines/`
    );

  getProductsRequest = (isInstructor, team_id) =>
    getRequest(
      isInstructor
        ? `${process.env.REACT_APP_API_URL}team/products/?round_lte=${this.activeRound}&team_id=${team_id}`
        : `${process.env.REACT_APP_API_URL}team/products/?round_lte=${this.activeRound}`
    );

  getAuditTrailRequest = (isInstructor, team_id) => {
    const game_id = Auth.getGameId();
    const requests = [];
    for (let i = 1; i <= this.activeRound; i++) {
      requests.push(
        isInstructor
          ? `${process.env.REACT_APP_API_URL}games/${game_id}/journal/?round_number=${i}&team_id=${team_id}`
          : `${process.env.REACT_APP_API_URL}games/${game_id}/journal/?round_number=${i}`
      );
    }
    return batchRequest(requests);
  };

  // Helper used by every formatter: resolve the user name from the
  // journal entry's user id, falling back to "Unknown" so the row
  // never renders blank.
  whoFromUserId = userId => {
    const user = this.state.users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : "Unknown";
  };

  // ---------- CEO: /api/team/projections/ -----------------------------

  get ceoEvents() {
    const { histories } = this.state;
    return histories
      .filter(h =>
        h.path.startsWith("/api/team/projections/") &&
        ((h.method === "POST" && h.status_code === 201) ||
          (h.method === "PATCH" && h.status_code === 200)) &&
        h.round_number === this.activeRound
      )
      .map((h, idx) => {
        const t = moment(h.requested_at);
        const resp = h.response || {};
        const targetRoundNumber = resp.target_round_number;
        const projectionLabel = targetRoundNumber != null
          ? `Projection for Year ${targetRoundNumber}`
          : "Projection";

        return {
          id: `ceo-${idx}-${h.requested_at}`,
          when: fmtTimestamp(h.requested_at),
          unix: t.unix(),
          who: this.whoFromUserId(h.user),
          action: h.method === "POST" ? "Created" : "Updated",
          description: projectionLabel,
          amount: "—",
          status: STATUS_SUCCESS,
          detail: {
            "Average Unit Costs": resp.average_unit_costs != null
              ? fmtMoneyDecimals(resp.average_unit_costs)
              : null,
            "Other Expenses": resp.other_operating_costs != null
              ? `$${(resp.other_operating_costs / 1e6).toFixed(2)}M`
              : null,
            "Average Selling Price": resp.average_selling_price != null
              ? fmtMoneyDecimals(resp.average_selling_price)
              : null,
            "Units Sold": resp.units_sold != null
              ? `${(resp.units_sold / 1e6).toFixed(3)}M`
              : null,
          },
          round: h.round_number,
        };
      });
  }

  // ---------- PD: /api/team/products/ + submit_development/ -----------

  get pdEvents() {
    const { histories } = this.state;
    const productNames = { ...this.state.products };
    const events = [];

    // R&D cost comes straight off the journal response payload for
    // POST/PATCH. DELETE has no response body, so the amount falls
    // through to "—".
    const rndCostFromResp = resp => {
      const v = resp && resp.rnd_costs;
      if (v == null) return "—";
      return fmtMoney(Math.abs(Number(v) || 0));
    };

    histories
      .filter(h =>
        ((h.path.startsWith("/api/team/products/") &&
          ((h.method === "POST" && h.status_code === 201) ||
           (h.method === "PATCH" && h.status_code === 200) ||
           (h.method === "DELETE" && h.status_code === 204))) ||
          h.path === "/api/teams/submit_development/") &&
        h.round_number === this.activeRound
      )
      .forEach((h, idx) => {
        const t = moment(h.requested_at);
        const base = {
          id: `pd-${idx}-${h.requested_at}`,
          when: fmtTimestamp(h.requested_at),
          unix: t.unix(),
          who: this.whoFromUserId(h.user),
          round: h.round_number,
        };

        if (h.path === "/api/teams/submit_development/") {
          const success = h.response && h.response.success === true;
          events.push({
            ...base,
            action: "Submitted",
            description: success ? "Submission was successful" : "Submission failed",
            amount: "—",
            status: success ? STATUS_SUCCESS : STATUS_FAILURE,
            detail: success
              ? null
              : { Error: (h.response && h.response.message) || "Unknown error" },
          });
          return;
        }

        if (h.method === "POST") {
          const data = jsonDecode(h.data || "{}");
          const resp = h.response || {};
          if (resp.id != null) {
            productNames[resp.id] = data.name || resp.name;
          }
          const name = (data && data.name) || resp.name || "Unnamed";
          events.push({
            ...base,
            action: "Created",
            description: `Updated the features' quality for product **${name}** to Resolution: ${resp.pixels_rank}, Sensitivity: ${resp.sensitivity_rank}, Power: ${resp.power_rank}, Temperature: ${resp.temp_range_rank}`,
            amount: rndCostFromResp(resp),
            status: STATUS_SUCCESS,
            detail: null,
          });
          return;
        }

        if (h.method === "PATCH") {
          const resp = h.response || {};
          const name = productNames[resp.id] || resp.name || "Product";
          events.push({
            ...base,
            action: "Updated",
            description: `Updated the features' quality for product **${name}** to Resolution: ${resp.pixels_rank}, Sensitivity: ${resp.sensitivity_rank}, Power: ${resp.power_rank}, Temperature: ${resp.temp_range_rank}`,
            amount: rndCostFromResp(resp),
            status: STATUS_SUCCESS,
            detail: null,
          });
          return;
        }

        if (h.method === "DELETE") {
          const match = h.path.match(/\/api\/team\/products\/(\d+)\//i);
          const productId = match ? parseInt(match[1], 10) : null;
          const name = (productId != null && productNames[productId]) || "Product";
          events.push({
            ...base,
            action: "Deleted",
            description: `Deleted product **${name}**`,
            amount: "—",
            status: STATUS_SUCCESS,
            detail: null,
          });
        }
      });

    return events;
  }

  // ---------- PM: journal-based event stream --------------------------
  // Marketing audit trail tracks every POST/PUT/DELETE on price_forecasts,
  // promos, and sales — including the bulk endpoints, which fan out into
  // one row per item in the request body. Product names come from
  // `products` (already keyed by id) and channel names from the
  // marketing_clients / sales_clients lookups loaded off team-decisions
  // role=marketing. Sales channels fall back to a hard-coded id→name
  // map (3 = Direct Sales, 4 = Distributors) since static endpoints
  // sometimes omit the list.

  pmProductName = id => {
    // Journal payload can carry product as either a number or a quoted
    // string (e.g. '31955'). The products map is keyed by numeric id,
    // so coerce before lookup. Both numeric and string keys are
    // accepted as a safety net.
    if (id == null) return "product";
    const numKey = Number(id);
    return (
      this.state.products[numKey] ||
      this.state.products[id] ||
      "product"
    );
  };

  pmMarketingClientName = id => {
    const c = (this.state.marketing_clients || []).find(c => c.id === id);
    return c ? c.name : `Client ${id}`;
  };

  pmSalesClientName = id => {
    const c = (this.state.sales_clients || []).find(c => c.id === id);
    if (c) return c.name;
    if (Number(id) === 3) return "Direct Sales";
    if (Number(id) === 4) return "Distributors";
    return `Client ${id}`;
  };

  get pmEvents() {
    const { histories } = this.state;
    const events = [];

    const isPmPath = path =>
      path.startsWith("/api/team/price_forecasts/") ||
      path.startsWith("/api/team/promos/") ||
      path.startsWith("/api/team/sales/") ||
      path === "/api/teams/submit_marketing/";

    histories
      .filter(h => h.round_number === this.activeRound && isPmPath(h.path))
      .filter(h => {
        // Only successful writes belong in the audit trail. 2xx and 204
        // pass; 4xx/5xx don't.
        const code = h.status_code;
        return code >= 200 && code < 300;
      })
      .forEach((h, idx) => {
        const t = moment(h.requested_at);
        const base = {
          when: fmtTimestamp(h.requested_at),
          unix: t.unix(),
          who: this.whoFromUserId(h.user),
          round: h.round_number,
        };
        const data = h.data ? jsonDecode(h.data) : {};
        const resp = h.response || {};

        // ----- /api/team/price_forecasts/[bulk|N]/ ----------------------
        if (h.path.startsWith("/api/team/price_forecasts/")) {
          if (h.path === "/api/team/price_forecasts/bulk/") {
            (data.forecasts || []).forEach((f, fIdx) => {
              const name = this.pmProductName(f.product);
              const price = Number(f.price) || 0;
              const forecast = Number(f.forecast) || 0;
              const isZero = price === 0 && forecast === 0;
              events.push({
                ...base,
                id: `pm-pfb-${idx}-${fIdx}-${h.requested_at}`,
                action: isZero ? "Deleted" : "Updated",
                description: isZero
                  ? `Deleted the price forecast decision for product **${name}**, dropping its selling price and unit forecast to zero.`
                  : `Updated product **${name}** selling price to ${fmtMoneyDecimals(price)} and the unit forecast to ${numberWithCommas(forecast)}.`,
                amount: "—",
                status: STATUS_NEUTRAL,
                detail: null,
              });
            });
            return;
          }
          // Individual price_forecast endpoint. The journal body shape
          // is {product, price, forecast}; we prefer the request body
          // over the response so DELETEs (which have no response body)
          // and bare POSTs still resolve correctly.
          if (h.method === "DELETE") {
            events.push({
              ...base,
              id: `pm-pf-${idx}-${h.requested_at}`,
              action: "Deleted",
              description: "Deleted a price forecast decision, dropping its selling price and unit forecast to zero.",
              amount: "—",
              status: STATUS_NEUTRAL,
              detail: null,
            });
            return;
          }
          const pid = data.product != null ? data.product : resp.product;
          const price = Number(data.price != null ? data.price : resp.price) || 0;
          const forecast = Number(
            data.forecast != null ? data.forecast :
            resp.forecast != null ? resp.forecast : 0
          );
          const name = this.pmProductName(pid);
          events.push({
            ...base,
            id: `pm-pf-${idx}-${h.requested_at}`,
            action: h.method === "POST" ? "Created" : "Updated",
            description: `Updated product **${name}** selling price to ${fmtMoneyDecimals(price)} and the unit forecast to ${numberWithCommas(forecast)}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          });
          return;
        }

        // ----- /api/team/promos/[bulk|N]/ -------------------------------
        if (h.path.startsWith("/api/team/promos/")) {
          if (h.path === "/api/team/promos/bulk/") {
            const pid = data.product;
            const name = this.pmProductName(pid);
            (data.spends || []).forEach((s, sIdx) => {
              const spend = Number(s.spend) || 0;
              const clientName = this.pmMarketingClientName(s.client);
              events.push({
                ...base,
                id: `pm-prb-${idx}-${sIdx}-${h.requested_at}`,
                action: spend === 0 ? "Deleted" : "Updated",
                description: spend === 0
                  ? `Deleted the spend allocation in ${clientName} for product **${name}**.`
                  : `Adjusted the spend allocation for product **${name}** to ${fmtMoney(spend)} in ${clientName}.`,
                amount: spend === 0 ? "—" : fmtMoney(spend),
                status: STATUS_NEUTRAL,
                detail: null,
              });
            });
            return;
          }
          if (h.method === "DELETE") {
            // The individual DELETE doesn't carry product/client in the
            // path or response, so the message stays generic.
            events.push({
              ...base,
              id: `pm-pr-${idx}-${h.requested_at}`,
              action: "Deleted",
              description: "Deleted a promotional spend allocation.",
              amount: "—",
              status: STATUS_NEUTRAL,
              detail: null,
            });
            return;
          }
          const pid = resp.product != null ? resp.product : data.product;
          const cid = resp.client != null ? resp.client : data.client;
          const spend = Number(resp.spend != null ? resp.spend : data.spend) || 0;
          const name = this.pmProductName(pid);
          const clientName = this.pmMarketingClientName(cid);
          events.push({
            ...base,
            id: `pm-pr-${idx}-${h.requested_at}`,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Spent ${fmtMoney(spend)} in ${clientName} to promote product **${name}**.`
              : `Adjusted the spend allocation for product **${name}** to ${fmtMoney(spend)} in ${clientName}.`,
            amount: fmtMoney(spend),
            status: STATUS_NEUTRAL,
            detail: null,
          });
          return;
        }

        // ----- /api/team/sales/[bulk|N]/ --------------------------------
        if (h.path.startsWith("/api/team/sales/")) {
          if (h.path === "/api/team/sales/bulk/") {
            // Atomic replace — group entries by product so each product
            // becomes one row listing the channels selected for it.
            const byProduct = {};
            (data.sales || []).forEach(s => {
              const pid = s.product;
              if (!byProduct[pid]) byProduct[pid] = [];
              byProduct[pid].push(s.client);
            });
            Object.keys(byProduct).forEach((pidStr, pIdx) => {
              const pid = Number(pidStr);
              const name = this.pmProductName(pid);
              const clients = byProduct[pid] || [];
              if (clients.length === 0) {
                events.push({
                  ...base,
                  id: `pm-sb-${idx}-${pIdx}-${h.requested_at}`,
                  action: "Deleted",
                  description: `Channel Strategy deleted for product **${name}**.`,
                  amount: "—",
                  status: STATUS_NEUTRAL,
                  detail: null,
                });
                return;
              }
              const clientList = clients
                .map(c => this.pmSalesClientName(c))
                .join(" and ");
              events.push({
                ...base,
                id: `pm-sb-${idx}-${pIdx}-${h.requested_at}`,
                action: "Updated",
                description: `Adjusted the channel strategy for product **${name}** to ${clientList}.`,
                amount: "—",
                status: STATUS_NEUTRAL,
                detail: null,
              });
            });
            return;
          }
          if (h.method === "DELETE") {
            events.push({
              ...base,
              id: `pm-sa-${idx}-${h.requested_at}`,
              action: "Deleted",
              description: "Channel Strategy deleted.",
              amount: "—",
              status: STATUS_NEUTRAL,
              detail: null,
            });
            return;
          }
          const pid = resp.product != null ? resp.product : data.product;
          const cid = resp.client != null ? resp.client : data.client;
          const name = this.pmProductName(pid);
          const clientName = this.pmSalesClientName(cid);
          events.push({
            ...base,
            id: `pm-sa-${idx}-${h.requested_at}`,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Selected ${clientName} as the channel strategy for product **${name}**.`
              : `Adjusted the channel strategy for product **${name}** to ${clientName}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          });
          return;
        }

        // ----- /api/teams/submit_marketing/ -----------------------------
        if (h.path === "/api/teams/submit_marketing/") {
          const success = h.response && h.response.success === true;
          events.push({
            ...base,
            id: `pm-submit-${idx}-${h.requested_at}`,
            action: "Submitted",
            description: success ? "Submission was successful" : "Submission failed",
            amount: "—",
            status: success ? STATUS_SUCCESS : STATUS_FAILURE,
            detail: success
              ? null
              : { Error: (h.response && h.response.message) || "Unknown error" },
          });
        }
      });

    return events;
  }

  // ---------- OP: machines + product_manufactures + submit_operations -
  // The production-line number (1..N) shown in descriptions doesn't
  // come from the path id — that's the DB pk. For machine purchases,
  // the journal `data` body carries `machine` as the line number; for
  // sells we extract the path id and resolve the line via two lookup
  // sources, in order: team_machines (current list) and a journal-walk
  // map built from prior /api/team/machines/ POSTs.
  //
  // For DELETE on /product_manufactures/<id>/, the path tells us the
  // pm row id but not the product. We build a {pm_id → product_id}
  // map from prior POST/PUT responses in this round so the description
  // can name the product even after deletion.
  //
  // /get_max_allowed/ and /api/team/factories/ are not user-driven
  // decisions and are filtered out entirely.

  get opEvents() {
    const { histories, team_machines, distribution_centers_info } = this.state;

    // machineDbId → production line number
    const machineDbToLine = {};
    team_machines.forEach(m => {
      if (m && m.id != null) machineDbToLine[m.id] = m.machine;
    });
    histories.forEach(h => {
      if (h.path === "/api/team/machines/" && h.method === "POST"
          && h.response && h.response.id != null) {
        machineDbToLine[h.response.id] = h.response.machine;
      }
    });

    // product_manufactures row id → product id (for DELETE lookups)
    const pmIdToProduct = {};
    histories.forEach(h => {
      const isPmCreate = h.path === "/api/team/product_manufactures/"
        && h.method === "POST";
      const isPmUpdate = /^\/api\/team\/product_manufactures\/\d+\/?$/i.test(h.path)
        && h.method === "PUT";
      if ((isPmCreate || isPmUpdate)
          && h.response && h.response.id != null
          && h.response.product != null) {
        pmIdToProduct[h.response.id] = h.response.product;
      }
    });

    // Distribution-center static lookup: id → display name. Used to
    // surface a readable location ("Toledo", "Little Rock") rather
    // than a raw id in team-center rows.
    const dcNameById = {};
    (distribution_centers_info || []).forEach(dc => {
      if (dc && dc.id != null) {
        dcNameById[dc.id] = dc.name || dc.location || `DC ${dc.id}`;
      }
    });
    // Returns the resolved DC display name or null if we couldn't
    // figure out which DC this row refers to. The render branches
    // adapt their copy when null is returned (rather than splicing
    // "DC undefined" or a stale id into the sentence).
    const dcName = id => {
      if (id == null) return null;
      return dcNameById[id] || null;
    };

    // team_center row id → distribution_center id (for DELETE lookups)
    // The DC id can show up under several field names depending on the
    // serializer (`distribution_center` from the model, `distributionCenter`
    // camelCased, or just echoed from the request body). Check all of
    // them so the DELETE row can still name the DC.
    const teamCenterToDc = {};
    histories.forEach(h => {
      if (h.path === "/api/v2/distributions/team-centers/"
          && h.method === "POST"
          && h.response && h.response.id != null) {
        const data = h.data ? jsonDecode(h.data) : {};
        const dcId =
          h.response.distribution_center != null ? h.response.distribution_center :
          h.response.distributionCenter   != null ? h.response.distributionCenter :
          data.distribution_center        != null ? data.distribution_center :
          data.distributionCenter         != null ? data.distributionCenter :
          null;
        if (dcId != null) {
          teamCenterToDc[h.response.id] = dcId;
          teamCenterToDc[String(h.response.id)] = dcId;
        }
      }
    });

    return histories
      .filter(h => h.round_number === this.activeRound)
      .filter(h => !h.path.includes("/get_max_allowed/"))
      .filter(h => !h.path.startsWith("/api/team/factories/"))
      .filter(h => {
        const isMachineBuy = h.path === "/api/team/machines/"
          && h.method === "POST" && h.status_code === 201;
        const isMachineSell = /\/api\/team\/machines\/\d+\/sell\/?$/i.test(h.path)
          && h.method === "POST";
        const isMachineUnsell = /\/api\/team\/machines\/\d+\/unsell\/?$/i.test(h.path)
          && h.method === "POST";
        const isPmCreate = h.path === "/api/team/product_manufactures/"
          && h.method === "POST" && h.status_code === 201;
        const isPmUpdate = /^\/api\/team\/product_manufactures\/\d+\/?$/i.test(h.path)
          && h.method === "PUT" && h.status_code === 200;
        const isPmDelete = /^\/api\/team\/product_manufactures\/\d+\/?$/i.test(h.path)
          && h.method === "DELETE";
        const isDcCreate = h.path === "/api/v2/distributions/team-centers/"
          && h.method === "POST" && h.status_code === 201;
        const isDcDelete = /^\/api\/v2\/distributions\/team-centers\/\d+\/?$/i.test(h.path)
          && h.method === "DELETE";
        const isSubmit = h.path === "/api/teams/submit_operations/";
        return isMachineBuy || isMachineSell || isMachineUnsell
          || isPmCreate || isPmUpdate || isPmDelete
          || isDcCreate || isDcDelete || isSubmit;
      })
      .map((h, idx) => {
        const t = moment(h.requested_at);
        const base = {
          id: `op-${idx}-${h.requested_at}`,
          when: fmtTimestamp(h.requested_at),
          unix: t.unix(),
          who: this.whoFromUserId(h.user),
          round: h.round_number,
        };
        const data = h.data ? jsonDecode(h.data) : {};
        const resp = h.response || {};

        // ----- /api/team/machines/ (acquire) ---------------------------
        if (h.path === "/api/team/machines/" && h.method === "POST") {
          const line = data.machine != null ? data.machine : resp.machine;
          return {
            ...base,
            action: "Created",
            description: `Acquired production line ${line != null ? line : "—"}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/machines/<id>/sell/ ---------------------------
        if (/\/api\/team\/machines\/\d+\/sell\/?$/i.test(h.path)) {
          const match = h.path.match(/\/api\/team\/machines\/(\d+)\/sell\/?$/i);
          const machineDbId = match ? parseInt(match[1], 10) : null;
          const line = machineDbId != null ? machineDbToLine[machineDbId] : null;
          return {
            ...base,
            action: "Deleted",
            description: `Sold production line ${line != null ? line : machineDbId}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/machines/<id>/unsell/ -------------------------
        if (/\/api\/team\/machines\/\d+\/unsell\/?$/i.test(h.path)) {
          const match = h.path.match(/\/api\/team\/machines\/(\d+)\/unsell\/?$/i);
          const machineDbId = match ? parseInt(match[1], 10) : null;
          const line = machineDbId != null ? machineDbToLine[machineDbId] : null;
          return {
            ...base,
            action: "Updated",
            description: `Canceled the sale of production line ${line != null ? line : machineDbId}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/product_manufactures/ (create) ----------------
        if (h.path === "/api/team/product_manufactures/" && h.method === "POST") {
          const productId = resp.product;
          const name = this.pmProductName(productId);
          const units = numberWithCommas(resp.units_produced);
          const line = machineDbToLine[resp.machine];
          return {
            ...base,
            action: "Created",
            description: `Updated production for product **${name}** to ${units} units on production line ${line != null ? line : "—"}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/product_manufactures/<id>/ (update) -----------
        if (/^\/api\/team\/product_manufactures\/\d+\/?$/i.test(h.path)
            && h.method === "PUT") {
          const productId = resp.product;
          const name = this.pmProductName(productId);
          const units = numberWithCommas(resp.units_produced);
          const line = machineDbToLine[resp.machine];
          return {
            ...base,
            action: "Updated",
            description: `Updated production for product **${name}** to ${units} units on production line ${line != null ? line : "—"}.`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/product_manufactures/<id>/ (delete) -----------
        if (/^\/api\/team\/product_manufactures\/\d+\/?$/i.test(h.path)
            && h.method === "DELETE") {
          const match = h.path.match(/^\/api\/team\/product_manufactures\/(\d+)\/?$/i);
          const pmId = match ? parseInt(match[1], 10) : null;
          const productId = pmId != null ? pmIdToProduct[pmId] : null;
          const name = productId != null
            ? this.pmProductName(productId)
            : "product";
          return {
            ...base,
            action: "Deleted",
            description: `Deleted production for product **${name}**`,
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/v2/distributions/team-centers/ (create) -----------
        if (h.path === "/api/v2/distributions/team-centers/" && h.method === "POST") {
          // Prefer the explicit name the backend echoes in response or
          // request body; only fall back to the id-based lookup against
          // distribution_centers_info when neither carries one.
          const explicitName =
            resp.distribution_center_name || data.distribution_center_name || null;
          const dcId =
            resp.distribution_center != null ? resp.distribution_center :
            data.distribution_center != null ? data.distribution_center :
            null;
          const name = explicitName || dcName(dcId);
          return {
            ...base,
            action: "Created",
            description: name
              ? `Selected distribution center ${name}`
              : "Selected a distribution center",
            amount: resp.cash_payment != null ? fmtMoney(resp.cash_payment) : "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/v2/distributions/team-centers/<id>/ (delete) ------
        if (/^\/api\/v2\/distributions\/team-centers\/\d+\/?$/i.test(h.path)
            && h.method === "DELETE") {
          const explicitName =
            resp.distribution_center_name || data.distribution_center_name || null;
          const dcIdFromBody =
            resp.distribution_center != null ? resp.distribution_center :
            data.distribution_center != null ? data.distribution_center :
            null;
          let dcId = dcIdFromBody;
          if (dcId == null) {
            // Older journal entries don't echo distribution_center on DELETE;
            // fall back to the walk-back map keyed by the team-center row id
            // pulled from the URL path.
            const match = h.path.match(/^\/api\/v2\/distributions\/team-centers\/(\d+)\/?$/i);
            const teamCenterId = match ? parseInt(match[1], 10) : null;
            if (teamCenterId != null) dcId = teamCenterToDc[teamCenterId];
          }
          const name = explicitName || dcName(dcId);
          return {
            ...base,
            action: "Deleted",
            description: name
              ? `Deleted distribution center ${name} decision`
              : "Deleted distribution center decision",
            amount: "—",
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/teams/submit_operations/ ---------------------------
        if (h.path === "/api/teams/submit_operations/") {
          const success = resp && resp.success === true;
          return {
            ...base,
            action: "Submitted",
            description: success ? "Submission was successful" : "Submission failed",
            amount: "—",
            status: success ? STATUS_SUCCESS : STATUS_FAILURE,
            detail: success
              ? null
              : { Error: (resp && resp.message) || "Unknown error" },
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  // ---------- FN: journal-based event stream --------------------------
  // Tracks the three finance write endpoints the user spec'd
  // (/equities/, /debts/, /debt_repayments/) plus the submit_finance
  // call. Counterparty names come from /static/equity_clients/ and
  // /static/debt_clients/. Any /api/static/debt_clients GETs that
  // leak into the journal are filtered out explicitly.

  get fnEvents() {
    const { histories, equity_clients, debt_clients } = this.state;

    const equityClientName = id => {
      const c = (equity_clients || []).find(c => c.id === id);
      return c ? c.name : `Investor ${id}`;
    };
    const debtClientName = id => {
      const c = (debt_clients || []).find(c => c.id === id);
      return c ? c.name : `Lender ${id}`;
    };

    return histories
      .filter(h => h.round_number === this.activeRound)
      .filter(h => !h.path.startsWith("/api/static/debt_clients"))
      .filter(h => {
        // Audit trail surfaces accepted writes only — failed (4xx)
        // requests on these endpoints carry an error-shaped response
        // body, not the model fields, so letting them through would
        // print things like "Issued shares to Investor undefined".
        // The submit_finance row is exempt because the audit trail
        // intentionally shows successful AND failed submissions.
        const ok = h.status_code >= 200 && h.status_code < 300;
        const isSubmit = h.path === "/api/teams/submit_finance/";
        if (!ok && !isSubmit) return false;

        const equityCreate = h.path === "/api/team/equities/" && h.method === "POST";
        const equityUpdate = /^\/api\/team\/equities\/\d+\/?$/i.test(h.path) && h.method === "PUT";
        const debtCreate = h.path === "/api/team/debts/" && h.method === "POST";
        const debtUpdate = /^\/api\/team\/debts\/\d+\/?$/i.test(h.path) && h.method === "PUT";
        const repayCreate = h.path === "/api/team/debt_repayments/" && h.method === "POST";
        const repayUpdate = /^\/api\/team\/debt_repayments\/\d+\/?$/i.test(h.path) && h.method === "PUT";
        const interestCreate = h.path === "/api/v2/finance/interest-payments/" && h.method === "POST";
        const interestUpdate = /^\/api\/v2\/finance\/interest-payments\/\d+\/?$/i.test(h.path) && h.method === "PUT";
        return equityCreate || equityUpdate || debtCreate || debtUpdate
          || repayCreate || repayUpdate
          || interestCreate || interestUpdate
          || isSubmit;
      })
      .map((h, idx) => {
        const t = moment(h.requested_at);
        const base = {
          id: `fn-${idx}-${h.requested_at}`,
          when: fmtTimestamp(h.requested_at),
          unix: t.unix(),
          who: this.whoFromUserId(h.user),
          round: h.round_number,
        };
        const resp = h.response || {};

        // ----- /api/team/equities/ ------------------------------------
        if (h.path.startsWith("/api/team/equities/")) {
          const clientName = equityClientName(resp.client);
          const issuance = numberWithCommas(resp.issuance);
          const amount = resp.total_capital != null
            ? fmtMoney(resp.total_capital)
            : "—";
          return {
            ...base,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Issued ${issuance} shares to ${clientName}`
              : `Adjusted issuance to ${issuance} shares to ${clientName}`,
            amount,
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/debts/ ---------------------------------------
        // Path prefix `/api/team/debts/` does not match
        // `/api/team/debt_repayments/` (the 15th char differs).
        if (h.path.startsWith("/api/team/debts/")) {
          const clientName = debtClientName(resp.client);
          const amount = resp.new_amount != null
            ? fmtMoney(resp.new_amount)
            : "—";
          return {
            ...base,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Borrowed from ${clientName}`
              : `Adjusted the borrowing amount from ${clientName}`,
            amount,
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/team/debt_repayments/ -----------------------------
        if (h.path.startsWith("/api/team/debt_repayments/")) {
          const clientName = debtClientName(resp.client);
          const amount = resp.amount != null
            ? fmtMoney(resp.amount)
            : "—";
          return {
            ...base,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Made a payment to reduce outstanding loan balance from ${clientName}`
              : `Adjusted loan repayment to ${clientName}`,
            amount,
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/v2/finance/interest-payments/ ---------------------
        if (h.path.startsWith("/api/v2/finance/interest-payments/")) {
          const clientName = debtClientName(resp.client);
          const amount = resp.amount != null
            ? fmtMoney(resp.amount)
            : "—";
          return {
            ...base,
            action: h.method === "POST" ? "Created" : "Updated",
            description: h.method === "POST"
              ? `Made a payment to reduce outstanding loan balance from ${clientName}`
              : `Adjusted loan repayment to ${clientName}`,
            amount,
            status: STATUS_NEUTRAL,
            detail: null,
          };
        }

        // ----- /api/teams/submit_finance/ -----------------------------
        if (h.path === "/api/teams/submit_finance/") {
          const success = resp && resp.success === true;
          return {
            ...base,
            action: "Submitted",
            description: success ? "Submission was successful" : "Submission failed",
            amount: "—",
            status: success ? STATUS_SUCCESS : STATUS_FAILURE,
            detail: success
              ? null
              : { Error: (resp && resp.message) || "Unknown error" },
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  eventsForRole = role => {
    if (role === "ceo") return this.ceoEvents;
    if (role === "pd")  return this.pdEvents;
    if (role === "pm")  return this.pmEvents;
    if (role === "op")  return this.opEvents;
    if (role === "fn")  return this.fnEvents;
    return [];
  };

  render() {
    const { location, currentRound, roundInUrl } = this.props;
    const routes = location.pathname.split("/").filter(i => i !== "");
    const role = routes[0];
    const activeRound = roundInUrl != null && roundInUrl !== currentRound
      ? roundInUrl
      : currentRound;

    const events = this.eventsForRole(role);

    return (
      <div className="dashboard">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            paddingTop: "30px",
          }}
        >
          <div className="team-reports-menu">
            <div style={{ display: "flex", flexDirection: "column" }}>
              {ROLES.map(r => (
                <Link
                  key={r.key}
                  to={`${r.path}?round=${activeRound}`}
                  className={role === r.key ? "active" : null}
                  style={{ paddingLeft: "15px" }}
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
          <div
            style={{
              background: "#F5F7FB",
              padding: "24px",
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
              width: "100%",
              minHeight: "70vh",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "rgba(0, 27, 59, 0.94)",
                marginBottom: 4,
              }}
            >
              Audit Trail
            </div>
            <div
              style={{
                fontSize: 13,
                color: "rgba(0, 27, 59, 0.6)",
                marginBottom: 18,
              }}
            >
              Every post, update, and delete made by team members in this round.
            </div>
            <EventTable events={events} />
          </div>
        </div>
      </div>
    );
  }
}

export default AuditTrail;



// WEBPACK FOOTER //
// ./src/components/audit-trail/page.js